/**
 * Recover when a deploy removes the files an open tab still needs.
 *
 * A page that loads code on demand asks for a hashed file like `TopicReader-a1b2.js` long
 * after it first loaded. If a deploy lands in between, that file is gone, and the import
 * fails as "Failed to fetch dynamically imported module" or, in Safari, "Importing a module
 * script failed". In the study app, which shares our PostHog project, that one class was
 * about 14,000 errors a week. Vite reports it as a `vite:preloadError` event; the fix is to
 * reload the page once, which fetches the new files.
 *
 * Once: if the reload itself lands on a broken deploy, reloading again would loop. So a
 * second failure within `RELOAD_WINDOW_MS` of the last reload is let through as an error.
 */

export const RELOAD_WINDOW_MS = 60_000;
export const RELOAD_KEY = 'labs:stale-asset-reload-at';

/** Pure: reload unless we already reloaded for this within the window. */
export function shouldReload(lastReloadAt: number | null, now: number): boolean {
  return lastReloadAt === null || !Number.isFinite(lastReloadAt) || now - lastReloadAt > RELOAD_WINDOW_MS;
}

interface StaleAssetContext {
  addEventListener: (type: 'vite:preloadError', listener: (event: Event) => void) => void;
  storage: Pick<Storage, 'getItem' | 'setItem'> | null;
  now: () => number;
  reload: () => void;
}

/** The imperative shell. Call once at startup; `window` supplies the default context. */
export function reloadOnStaleAssets(context: StaleAssetContext = {
  addEventListener: (type, listener) => window.addEventListener(type, listener),
  storage: (() => { try { return window.sessionStorage; } catch { return null; } })(),
  now: Date.now,
  reload: () => window.location.reload(),
}): void {
  context.addEventListener('vite:preloadError', (event) => {
    const stored = safely(() => context.storage?.getItem(RELOAD_KEY) ?? null);
    const last = stored === null || stored === undefined ? null : Number(stored);
    const now = context.now();
    if (!shouldReload(last, now)) return; // Let it surface: this is a real failure, not a stale tab.
    event.preventDefault();
    safely(() => context.storage?.setItem(RELOAD_KEY, String(now)));
    context.reload();
  });
}

function safely<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}
